import { useEffect, useState } from "react";
import { api } from "../api.js";

/**
 * Talks to the recorder extension.
 *
 * The portal is an ordinary page, so it cannot address the extension by id —
 * an unpacked extension's id depends on where the folder lives. Instead the
 * extension's content script is already injected here and relays window
 * messages, so the page just shouts into `window` and listens for the reply.
 */

const PORTAL = "veyso-portal";
const EXTENSION = "veyso-extension";

interface ExtensionMessage {
  source: string;
  type: string;
  version?: string;
  projectId?: string;
  message?: string;
}

function post(message: Record<string, unknown>): void {
  window.postMessage({ source: PORTAL, ...message }, window.location.origin);
}

/**
 * Whether the extension is installed, discovered by asking and waiting briefly.
 * It also announces itself on load, so installing it while this page is open is
 * noticed without a refresh.
 */
export function useRecorderExtension(): { available: boolean; version: string | null } {
  const [state, setState] = useState<{ available: boolean; version: string | null }>({
    available: false,
    version: null,
  });

  useEffect(() => {
    let settled = false;

    /**
     * The content script stamps the version onto <html> as soon as it runs.
     * Reading an attribute has no ordering problem, unlike a ping/pong that
     * loses whichever message was sent before the other side was listening.
     */
    const readMarker = () => {
      const version = document.documentElement.dataset.veysoRecorder;
      if (!version) return false;
      settled = true;
      setState({ available: true, version });
      return true;
    };

    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window || event.data?.source !== EXTENSION) return;
      if (event.data.type === "pong") {
        settled = true;
        setState({ available: true, version: event.data.version ?? null });
      }
    };

    window.addEventListener("message", onMessage);

    if (!readMarker()) {
      // Content scripts run at document_idle, which can be after React mounts.
      // Poll briefly rather than assume the extension is absent.
      post({ type: "ping" });
      const poll = setInterval(() => {
        if (settled || readMarker()) clearInterval(poll);
        else post({ type: "ping" });
      }, 400);
      const stop = setTimeout(() => clearInterval(poll), 4000);

      return () => {
        window.removeEventListener("message", onMessage);
        clearInterval(poll);
        clearTimeout(stop);
      };
    }

    return () => window.removeEventListener("message", onMessage);
  }, []);

  return state;
}

/**
 * Nominates a project for the next recording.
 *
 * Chrome deliberately refuses to let a page start a tab capture — that has to
 * come from a gesture inside the extension — so this only sets the target. The
 * user then presses record from the tab they want to demonstrate.
 */
export function armRecorder(projectId: string, projectName: string): Promise<boolean> {
  return new Promise((resolve) => {
    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window || event.data?.source !== EXTENSION) return;
      if (event.data.type === "armed" && event.data.projectId === projectId) {
        window.removeEventListener("message", onMessage);
        clearTimeout(timeout);
        resolve(true);
      }
      if (event.data.type === "error") {
        window.removeEventListener("message", onMessage);
        clearTimeout(timeout);
        resolve(false);
      }
    };

    const timeout = setTimeout(() => {
      window.removeEventListener("message", onMessage);
      resolve(false);
    }, 2000);

    window.addEventListener("message", onMessage);
    post({ type: "arm", projectId, projectName });
  });
}

/**
 * Hands the recorder a credential of its own, once it is installed.
 *
 * The extension cannot sign in by itself. It is a different origin from the
 * app, so it can read none of the session — and it needs something that
 * outlives a page anyway, because a recording started an hour later must not
 * fail on an expired token.
 *
 * So the app mints one and passes it over the bridge the extension already
 * listens on. The result is that installing the extension and being signed in
 * is the whole of the setup: no address to type, no token to copy, and a
 * recording that lands in the account it was made from rather than in nobody's.
 *
 * Minted only when the extension says it has none. A token is replaced by
 * issuing a new one, so handing one over on every page load would quietly
 * unpair whichever other browser was holding the last.
 */
export function useRecorderHandover(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let handed = false;

    const onMessage = async (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window || event.data?.source !== EXTENSION) return;
      if (event.data.type !== "needs-credential" || cancelled || handed) return;

      // Once per page, whatever the extension asks. It answers every check,
      // and minting twice would retire the token handed over a moment ago.
      handed = true;
      try {
        const { token } = await api.recorderToken();
        if (!cancelled) post({ type: "credential", token });
      } catch {
        // The extension keeps its own state and the popup offers to sign in.
        // An error on a page somebody was not using the recorder from is
        // noise.
        handed = false;
      }
    };

    window.addEventListener("message", onMessage);

    /**
     * Asked repeatedly for a few seconds, not once.
     *
     * Content scripts run at `document_idle`, which can be after React has
     * mounted — so a single check on mount is a message shouted before the
     * other side is listening. `useRecorderExtension` polls for exactly this
     * reason and this used not to, which made the handover work or not
     * depending on how fast the page had loaded.
     *
     * It stops as soon as the extension answers, and gives up after a few
     * seconds rather than polling a browser that has no extension in it.
     */
    post({ type: "credential-check" });
    const poll = setInterval(() => {
      if (cancelled || handed) clearInterval(poll);
      else post({ type: "credential-check" });
    }, 500);
    const stop = setTimeout(() => clearInterval(poll), 5000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(stop);
      window.removeEventListener("message", onMessage);
    };
  }, [enabled]);
}
